import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, fmtMoney, Spinner, useAuth, useFetch } from '@greenrinse/ui';
import { startTour } from '@greenrinse/features';
import { ArrowRight, Camera, Car, CircleAlert, MessageSquareWarning, PlayCircle, Truck } from 'lucide-react';
import { useOpsHub } from './RecordsPage';
import { periodTone } from './ReconciliationPage';
import { DailyColumns, HBars, StatusSplit } from './DashboardCharts';
import './dashboard.css';

interface Money { key: string; label: string; currency: string; invoicedMinor: number; outstandingMinor: number }
interface Dashboard {
  today: { washes: number; verified: number; flagged: number; activeShifts: number; sitesInProgress: number; exceptionsWaiting: number; exceptionsOlderThan24h: number; openDisputes: number; pendingPhotos: number };
  period: { from: string; to: string; washes: number; byVehicleType: Record<string, number>; byService: Record<string, number>; byStatus: Record<string, number>;
    coverageByPartner: { partnerId: string; partnerName: string; fleetSize: number; vehiclesWashed: number; coveragePct: number; washes: number }[];
    reconciliation: { periodId: string; periodName: string; partnerName: string; status: string; billable: number; billableAmountMinor: number; currency: string | null; openDisputes: number }[];
    trend: { date: string; washes: number; flagged: number }[] };
  revenue: { baseCurrency: string; totalInvoicedBase: number; totalOutstandingBase: number; byPartner: Money[]; byCountry: Money[]; missingRates: string[] };
}

type Preset = 'month' | '7' | '30';
const PRESETS: { key: Preset; label: string }[] = [{ key: 'month', label: 'This month' }, { key: '30', label: 'Last 30 days' }, { key: '7', label: 'Last 7 days' }];
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function rangeOf(p: Preset) {
  const now = new Date();
  const from = p === 'month' ? new Date(now.getFullYear(), now.getMonth(), 1) : new Date(now.getFullYear(), now.getMonth(), now.getDate() - Number(p) + 1);
  return { from: iso(from), to: iso(now) };
}
const greeting = () => { const h = new Date().getHours(); return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'; };

/** Sums per currency — used when an exchange rate to the base currency is missing (the base total would read 0). */
function byCurrency(rows: Money[], field: 'invoicedMinor' | 'outstandingMinor') {
  const m = new Map<string, number>();
  rows.forEach((r) => m.set(r.currency, (m.get(r.currency) ?? 0) + r[field]));
  return [...m.entries()];
}

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preset, setPreset] = useState<Preset>('month');
  const range = rangeOf(preset);
  const dash = useFetch<Dashboard>(`/api/dashboard?from=${range.from}&to=${range.to}`);
  useOpsHub(() => dash.reload());

  if (dash.loading && !dash.data) return <Spinner />;
  if (!dash.data) return <Alert tone="error">{dash.error ?? 'Could not load the dashboard'}</Alert>;
  const { today, period, revenue } = dash.data;
  const flagged = period.trend.reduce((s, p) => s + p.flagged, 0);
  const coverage = [...period.coverageByPartner].sort((a, b) => b.coveragePct - a.coveragePct);
  const useBase = revenue.missingRates.length === 0;
  const invoiced = useBase ? [[revenue.baseCurrency, revenue.totalInvoicedBase * 100] as const] : byCurrency(revenue.byCountry, 'invoicedMinor');
  const outstanding = useBase ? [[revenue.baseCurrency, revenue.totalOutstandingBase * 100] as const] : byCurrency(revenue.byCountry, 'outstandingMinor');

  const kpis = [
    { label: 'Washes today', value: today.washes, sub: `${today.verified} verified · ${today.flagged} flagged`, icon: <Car size={18} />, to: '/records' },
    { label: 'Active shifts', value: today.activeShifts, sub: `${today.sitesInProgress} sites in progress`, icon: <Truck size={18} />, to: '/tracking' },
    { label: 'Exceptions waiting', value: today.exceptionsWaiting, sub: today.exceptionsOlderThan24h > 0 ? `${today.exceptionsOlderThan24h} older than 24 h` : 'All recent', icon: <CircleAlert size={18} />, to: '/exceptions', warn: today.exceptionsOlderThan24h > 0, tour: 'kpi-exceptions' },
    { label: 'Open disputes', value: today.openDisputes, sub: today.openDisputes > 0 ? 'Needs a reply' : 'Nothing open', icon: <MessageSquareWarning size={18} />, to: '/disputes', warn: today.openDisputes > 0 },
    { label: 'Awaiting photos', value: today.pendingPhotos, sub: 'Uploading from phones', icon: <Camera size={18} />, to: '/records' },
  ];

  return (
    <div className="gr-stack gr-dash" style={{ opacity: dash.loading ? 0.6 : 1 }}>
      <div className="gr-dash__head">
        <div>
          <h1 style={{ marginBottom: 2 }}>{greeting()}, {user?.fullName?.split(' ')[0]}</h1>
          <span className="gr-muted">{new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })} · {t('nav.dashboard')}</span>
        </div>
        <div className="gr-dash__actions">
          <div className="gr-seg" role="group" aria-label="Date range">
            {PRESETS.map((p) => <button key={p.key} type="button" aria-pressed={preset === p.key} onClick={() => setPreset(p.key)}>{p.label}</button>)}
          </div>
          <Button variant="tonal" onClick={startTour}><PlayCircle size={18} aria-hidden />{t('tour.watch')}</Button>
        </div>
      </div>

      <div className="gr-dash__kpis" data-tour="kpis">
        {kpis.map((k) => (
          <button key={k.label} type="button" className={`gr-kpi ${k.warn ? 'gr-kpi--warn' : ''}`} data-tour={k.tour} onClick={() => navigate(k.to)}>
            <span className="gr-kpi__top"><span className="gr-kpi__icon" aria-hidden>{k.icon}</span>{k.label}</span>
            <span className="gr-kpi__value">{k.value}</span>
            <span className="gr-kpi__sub">{k.warn && <Badge tone="warning">Action</Badge>}{k.sub}</span>
          </button>
        ))}
      </div>

      <div className="gr-dash__row gr-dash__row--main">
        <Card className="gr-dash__card" data-tour="trend">
          <div className="gr-dash__cardhead">
            <div>
              <div className="gr-dash__title">Washes per day</div>
              <div className="gr-muted gr-dash__range">{range.from} → {range.to}</div>
            </div>
            <div className="gr-dash__hero"><span>{period.washes}</span><small className="gr-muted">washes · {flagged} flagged</small></div>
          </div>
          <ul className="gr-legend">
            <li><i className="gr-chart__key--s1" />Passed</li>
            <li><i className="gr-chart__key--s2" />Flagged</li>
          </ul>
          <DailyColumns points={period.trend} />
          <details className="gr-dash__table">
            <summary>Show as table</summary>
            <table className="gr-table">
              <thead><tr><th>Date</th><th style={{ textAlign: 'end' }}>Washes</th><th style={{ textAlign: 'end' }}>Flagged</th></tr></thead>
              <tbody>{period.trend.filter((p) => p.washes > 0).map((p) => <tr key={p.date}><td>{p.date}</td><td style={{ textAlign: 'end' }}>{p.washes}</td><td style={{ textAlign: 'end' }}>{p.flagged}</td></tr>)}</tbody>
            </table>
          </details>
        </Card>

        <Card className="gr-dash__card">
          <div className="gr-dash__title">Wash mix</div>
          <h4 className="gr-dash__sub">Review status</h4>
          <StatusSplit byStatus={period.byStatus} />
          <h4 className="gr-dash__sub">Vehicle types</h4>
          <HBars rows={Object.entries(period.byVehicleType).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }))} />
          <h4 className="gr-dash__sub">Services</h4>
          <HBars rows={Object.entries(period.byService).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }))} />
        </Card>
      </div>

      <div className="gr-dash__row">
        <Card className="gr-dash__card" data-tour="coverage">
          <div className="gr-dash__cardhead">
            <div><div className="gr-dash__title">Fleet coverage</div><div className="gr-muted gr-dash__range">Distinct vehicles washed per partner</div></div>
            <button type="button" className="gr-btn gr-btn--ghost gr-dash__link" onClick={() => navigate('/partners')}>All partners <ArrowRight size={16} /></button>
          </div>
          <ul className="gr-meters">
            {coverage.slice(0, 6).map((c) => (
              <li key={c.partnerId}>
                <button type="button" onClick={() => navigate(`/partners/${c.partnerId}`)}>
                  <span className="gr-meters__name">{c.partnerName}</span>
                  <span className="gr-meters__track"><span className={`gr-meters__fill ${c.coveragePct >= 95 ? 'is-done' : ''}`} style={{ width: `${Math.min(100, c.coveragePct)}%` }} /></span>
                  <span className="gr-meters__value"><strong>{c.coveragePct}%</strong> <span className="gr-muted">{c.vehiclesWashed}/{c.fleetSize}</span></span>
                </button>
              </li>
            ))}
            {coverage.length === 0 && <li className="gr-muted">No partners yet</li>}
          </ul>
        </Card>

        <Card className="gr-dash__card">
          <div className="gr-dash__cardhead">
            <div><div className="gr-dash__title">Revenue</div><div className="gr-muted gr-dash__range">Issued invoices in range</div></div>
            <button type="button" className="gr-btn gr-btn--ghost gr-dash__link" onClick={() => navigate('/reports')}>Details <ArrowRight size={16} /></button>
          </div>
          <div className="gr-dash__money">
            {invoiced.length === 0 ? <span>0</span> : invoiced.map(([cur, minor]) => <span key={cur}>{fmtMoney(minor, cur)}</span>)}
          </div>
          <div className="gr-muted" style={{ fontSize: 14 }}>
            Outstanding {outstanding.length === 0 ? '0' : outstanding.map(([cur, minor]) => fmtMoney(minor, cur)).join(' · ')}
          </div>
          {revenue.byPartner.length > 0 && <h4 className="gr-dash__sub">Top partners</h4>}
          {/* shortcut: bars compare invoicedMinor across partners as-is — fine while partners share one currency; use base amounts once FX rates exist */}
          <HBars rows={[...revenue.byPartner].sort((a, b) => b.invoicedMinor - a.invoicedMinor).slice(0, 5).map((r) => ({ label: r.label, value: r.invoicedMinor }))}
            format={(v) => fmtMoney(v, revenue.byPartner[0]?.currency ?? revenue.baseCurrency)} />
          {!useBase && <div className="gr-muted" style={{ fontSize: 12, marginTop: 10 }}>No exchange rate to {revenue.baseCurrency} for {revenue.missingRates.join(', ')}: totals shown per currency.</div>}
        </Card>
      </div>

      <Card className="gr-dash__card" style={{ padding: 0 }}>
        <div className="gr-dash__cardhead" style={{ padding: '18px 20px 8px' }}>
          <div className="gr-dash__title">Reconciliation</div>
          <button type="button" className="gr-btn gr-btn--ghost gr-dash__link" onClick={() => navigate('/reconciliation')}>All periods <ArrowRight size={16} /></button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="gr-table">
            <thead><tr><th>Period</th><th>Partner</th><th style={{ textAlign: 'end' }}>Billable</th><th style={{ textAlign: 'end' }}>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {period.reconciliation.slice(0, 6).map((p) => (
                <tr key={p.periodId} style={{ cursor: 'pointer' }} onClick={() => navigate(`/reconciliation/${p.periodId}`)}>
                  <td><strong>{p.periodName}</strong></td>
                  <td className="gr-muted">{p.partnerName}</td>
                  <td style={{ textAlign: 'end' }}>{p.billable}</td>
                  <td style={{ textAlign: 'end' }}>{p.currency ? fmtMoney(p.billableAmountMinor, p.currency) : '—'}</td>
                  <td><Badge tone={periodTone(p.status)}>{p.status}</Badge> {p.openDisputes > 0 && <Badge tone="error">{p.openDisputes} disputes</Badge>}</td>
                </tr>))}
              {period.reconciliation.length === 0 && <tr><td colSpan={5} className="gr-muted">No periods in range</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
